/**
 * Enhanced API Composable with Token Manager
 * - Uses centralized token manager
 * - Automatic token refresh with race condition prevention
 * - Automatic request retry after successful token refresh
 * - Request queue management
 */

import { createFetch } from '@vueuse/core'
import { destr } from 'destr'
import { tokenManager } from '@/utils/tokenManager'

export const useApi = createFetch({
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  fetchOptions: {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  },
  options: {
    refetch: true,

    async beforeFetch({ options }) {
      // Get valid token (with auto-refresh if needed)
      const accessToken = await tokenManager.getValidAccessToken()

      if (accessToken) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${accessToken}`,
        }
      }

      return { options }
    },

    afterFetch(ctx) {
      const { data, response } = ctx

      // Parse data if it's JSON
      let parsedData = null
      try {
        parsedData = destr(data)
      }
      catch (error) {
        console.error('Failed to parse response data:', error)
      }

      return { data: parsedData, response }
    },

    async onFetchError(ctx) {
      const { response, data } = ctx

      // Handle 401 Unauthorized
      if (response?.status === 401) {

        // Special handling for refresh token endpoint failure
        const isRefreshEndpoint = response.url.includes('/auth/refresh')
        const isLogoutEndpoint = response.url.includes('/auth/logout')
        
        if (isRefreshEndpoint) {
          
          // Clear tokens WITHOUT calling refresh again (prevent infinite loop)
          // Pass skipRedirect=true to prevent double redirect
          await tokenManager.clearTokens(true)
          
          // Force redirect to login page immediately - ALWAYS redirect regardless of current path
          if (typeof window !== 'undefined') {
            
            // Use replace() instead of href to prevent back button issues
            // Reduce delay to minimal amount
            window.location.replace('/login')
          }
          
          return ctx
        }

        // Special handling for logout endpoint - token already expired, no need to refresh
        if (isLogoutEndpoint) {
          
          return ctx
        }

        // For other endpoints, try to refresh token
        // Note: tokenManager has singleton pattern to prevent multiple refresh requests
        // If multiple requests get 401 simultaneously, they will wait for single refresh

        const refreshSuccess = await tokenManager.refreshToken()

        if (refreshSuccess) {

          // Note: VueUse useFetch doesn't support automatic retry in onFetchError
          // User needs to manually trigger the action again
          // For automatic retry, use $api from utils/api.ts instead
        }
        else {
          // If refresh failed, tokenManager already handled redirect
          // Token refresh failed - tokenManager will handle redirect
        }
      }

      // Handle 403 Forbidden
      if (response && response.status === 403) {
        console.error('❌ 403 Forbidden - Insufficient permissions')
        
        if (typeof window !== 'undefined') {
          const event = new CustomEvent('api-error', {
            detail: {
              status: 403,
              message: 'You do not have permission to perform this action',
            },
          })
          window.dispatchEvent(event)
        }
      }

      // Handle server errors
      if (response && response.status >= 500) {
        console.error('❌ Server error:', response.status)
        
        if (typeof window !== 'undefined') {
          const event = new CustomEvent('api-error', {
            detail: {
              status: response.status,
              message: 'Server error occurred. Please try again later.',
            },
          })
          window.dispatchEvent(event)
        }
      }

      return ctx
    },
  },
})
